/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleProps,  INodeProps } from '@msdyn365-commerce-modules/utilities';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IGridSettings, IImageSettings, isEmptyOrNullObject} from '@msdyn365-commerce/core';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import * as React from 'react';
import { Link } from './components/link';
import { SubLink } from './components/subLink';
import { IMenuItemData, ICittaCategoryNavigationMenuData } from './citta-category-navigation-menu.data';
import { ICittaCategoryNavigationMenuProps } from './citta-category-navigation-menu.props.autogenerated';

export interface  INavigationMenuViewProps extends ICittaCategoryNavigationMenuProps<ICittaCategoryNavigationMenuData> {
    className: string;
    menuItemData: IMenuItemData[];
    Navigation: IModuleProps;
    MenuList: INodeProps;
    ListItem: INodeProps;
    Link: INodeProps;
    Button: INodeProps;
    Span: INodeProps;
    ImageContainer:INodeProps;
    MobileDescriptionContainer:INodeProps;
    MobileBackButton:INodeProps;
    MobileDescriptionLabel:INodeProps;
    imageSettings: IImageSettings;
    gridSettings: IGridSettings;
    isMobileView: boolean;
    DivContainer?:INodeProps;
    ImageDivContainer?:INodeProps;
    showCategoryImage?:boolean;
    pageType: string;
    links?: React.ReactNode[];
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface INavigationMenuState {
    isMobile: boolean;
    linkHoverRecId?: number;
    subLinkHoverRecId?: number;
}

/**
 *
 * NavigationMenu component
 * @extends {React.PureComponent<ICittaCategoryNavigationMenuProps<ICittaCategoryNavigationMenuData>>}
 */
class NavigationMenu extends React.PureComponent<ICittaCategoryNavigationMenuProps<ICittaCategoryNavigationMenuData>, INavigationMenuState> {
    private resizeThrottledEventHandler?: (event: Event) => void;
    private hasSubLinks: boolean;
    @observable
    private viewport: GridSize;
    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: ICittaCategoryNavigationMenuProps<ICittaCategoryNavigationMenuData>) {
        super(props);
        this.viewport = props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this.state = {
            isMobile: this.isMobile,
            linkHoverRecId: undefined,
            subLinkHoverRecId: undefined

        };
        this.hasSubLinks = false;

        this._handleHover = this._handleHover.bind(this);

    }

    @computed get isMobile(): boolean {
        return (this.viewport === 'xs' || this.viewport === 'sm');
    }

    public componentDidMount(): void {
        let container = document.querySelector(".ms-nav-category");
        if (container) {
            const middle =  container.querySelector("a.active") || container.children[Math.floor((container.children.length - 1) / 2)];
           
            container.scrollTo({
                top: 0,
                 // @ts-ignore
                left:  middle.offsetLeft + middle.offsetWidth / 2 - container.offsetWidth / 2,
                behavior: 'smooth'
              })
            
            
            // middle.offsetLeft + middle.offsetWidth / 2 - container.offsetWidth / 2;
        }
    }

    public componentWillUnmount(): void {
        window && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
    }

    public render(): JSX.Element | null {
        const urlTokens = this.props.context.request.urlTokens;

        let links: React.ReactNode[] = [];

        if (urlTokens.pageType === 'Category') {
            const currentCategoryId = Number(urlTokens.recordId);
            const categoryHierarchyList = this.props.data.categories.result;
            if (categoryHierarchyList && categoryHierarchyList[1]) {
                const categoryHierarchyCurrent = categoryHierarchyList[1].Children[0];
                categoryHierarchyCurrent.Children.forEach(layerOneChild => {
                    
                    if (currentCategoryId === layerOneChild.RecordId) {
                        links = this._getLinkData(layerOneChild, currentCategoryId);
                    } else if (layerOneChild.Children && layerOneChild.Children.length) {
                        layerOneChild.Children.forEach(layerTwoChild => {
                            if (currentCategoryId === layerTwoChild.RecordId) {
                                links = this._getLinkData(layerOneChild, currentCategoryId);
        
                            } else if (layerTwoChild.Children && layerTwoChild.Children.length) {
                                layerTwoChild.Children.forEach(layerThreeChild => {
                                    if (currentCategoryId === layerThreeChild.RecordId) {
                                        links = this._getLinkData(layerOneChild, currentCategoryId);
                                    }
                                });
                            }
                        })
                    }
                })
            }
        }

        if (isEmptyOrNullObject(this.props.data)) {
            this.props.telemetry.error('Navigatiomenu returned null');
            return null;
        }

        const viewProps = {
            ...this.props,
            ...this.state,
            pageType: urlTokens.pageType,
            Navigation: {
                moduleProps: this.props,
                className: classnames('ms-nav-category', this.props.config.className, this.state.isMobile ? 'mobile-vp' : 'desktop-vp', this.hasSubLinks ? 'hasSubMenu' : ''),
                tag:'nav',
                role:'navigation',
                'aria-label': this.props.resources.menuAriaLabel
            },
            isMobileView: this.state.isMobile,
            links: links,
            className: classnames('ms-nav', this.props.config.className)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _handleHover(recordId?: number, isSubLink = false) {
        if (isSubLink) {
            this.setState({subLinkHoverRecId: recordId})
        } else  {
            this.setState({linkHoverRecId: recordId})
        }
    }

    private _getLinkData(category: CategoryHierarchy, activeCurrent: Number): React.ReactNode[] {
        let links = [];
        links.push(<Link
            isActive={category.RecordId === activeCurrent}
            text={category.Name}
            ariaLabel={category.Name}
            href={category.Url}
            index='1'
            className='ms-category_link'
            telemetryContent={{pageName: `'Category': ${category.Name}` , moduleName: 'citta category nav menu'}}
            handleHover={this._handleHover}
            recordId={category.RecordId}
            menuHover={!!this.state.linkHoverRecId || !!this.state.subLinkHoverRecId}
            isHover={this.state.linkHoverRecId === category.RecordId || this.state.subLinkHoverRecId === category.RecordId}
        />);
       
        (category.Children || []).forEach(childCategory => {
            let subLinks: any = [];
            let subLinkIsActive = false;
            if (childCategory.Children) {
                childCategory.Children.forEach(childChildCategory => {
                    if (childChildCategory.RecordId === activeCurrent) {
                        subLinkIsActive = true;
                    }
                    subLinks.push(<SubLink
                        isActive={childChildCategory.RecordId === activeCurrent}
                        text={childChildCategory.Name}
                        ariaLabel={childChildCategory.Name}
                        href={childChildCategory.Url}
                        index='1'
                        className='ms-category_sublink'
                        telemetryContent={{pageName: `'Category sublink': ${childChildCategory.Name}` , moduleName: 'citta category nav menu'}}
                        recordId={this.state.linkHoverRecId || 0}
                        handleHover={this._handleHover}
                    />);
                });
                
            }
            if ((childCategory.RecordId === activeCurrent || subLinkIsActive) && childCategory.Children && childCategory.Children.length ) {
                this.hasSubLinks = true;
            }

            links.push(<Link
                isActive={childCategory.RecordId === activeCurrent || subLinkIsActive}
                text={childCategory.Name}
                ariaLabel={childCategory.Name}
                href={childCategory.Url}
                index='1'
                className='ms-category_link'
                telemetryContent={{pageName: `'Category': ${childCategory.Name}` , moduleName: 'citta category nav menu'}}
                subLinks={subLinks}
                handleHover={this._handleHover}
                recordId={childCategory.RecordId}
                menuHover={!!this.state.linkHoverRecId || !!this.state.subLinkHoverRecId}
                isHover={this.state.linkHoverRecId === childCategory.RecordId || this.state.subLinkHoverRecId === category.RecordId}
            />);
        })

        return links;
    }
}

export default NavigationMenu;
